import { DeviceStatusInfo } from "@fairfleet/geotab/types";
import { UseQueryResult, keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { chunkArray } from "@/utilities/chunkArray";

export function useStatusInfosByIds(
  ids: string[],
  diagnosticIds: string[] = [],
  interval?: number,
): UseQueryResult<Record<string, DeviceStatusInfo>> {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["DeviceStatusInfo", activeSessionId, ids, diagnosticIds],

    queryFn: async ({ signal }) => {
      if (!ids || ids.length === 0) {
        return {};
      }

      const batchedIds = chunkArray(ids, 2000);

      // The batches are independent, so awaiting them one after another only added latency —
      // on a large fleet that is several seconds before the first row appears.
      const statusInfoBatches = await Promise.all(
        batchedIds.map(async batch => {
          const result = await geotabClient.get(
            "DeviceStatusInfo",
            { deviceSearch: { deviceIds: batch }, diagnostics: diagnosticIds.map(id => ({ id })) },
            batch.length,
            signal,
          );
          return result ?? [];
        }),
      );
      const statusInfoList = statusInfoBatches.flat();

      const statusInfoMap = statusInfoList.reduce<Record<string, DeviceStatusInfo>>(
        (map, statusInfo) => {
          if (statusInfo.device && statusInfo.device.id) {
            map[statusInfo.device.id] = statusInfo;
          }
          return map;
        },
        {},
      );

      return statusInfoMap;
    },

    refetchInterval: interval ?? 15000,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
    enabled: ids.length > 0,
  });
}
